// src/components/AuthPanelSimple.jsx
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import api from "../utils/api";
import UserContext from "../context/UserContext";
import "../assets/styles1010/UpdatedWelcomePage.css";
import GoogleG from "../assets/images/google-g.svg";

const HOME_PATH = "/";
const MOODCALL_PATH = "/moodcall";

export default function AuthPanelSimple() {
  const { setName, setReason, setAuthToken, sessionId, visitorId } = useContext(UserContext);

  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState(""); 
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pick = (e, fb) =>
    e?.response?.data?.message ||  
    e?.response?.data?.non_field_errors?.[0] ||
    e?.response?.data?.email?.[0] ||
    e?.response?.data?.password?.[0] ||
    e?.response?.data?.detail ||
    e?.response?.data?.error ||
    e?.message ||
    fb;

  const ensureCsrf = async () => { try { await api.get("/api/set-csrf/"); } catch {} };

  const fetchUser = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    try {
      const res = await api.get("/api/auth/user/", {
        headers: { Authorization: `Token ${token}` },
        withCredentials: true,
      });
      setName(res.data?.name || "");
      setReason(res.data?.reason || "");
    } catch {}
  };

  const hasToday = async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";
      const token = localStorage.getItem("authToken");
      const headers = token ? { Authorization: `Token ${token}` } : {};
      const { data } = await api.get("/api/mood/status/", { params: { tz }, headers });
      return !!data?.has_today;
    } catch { return false; }
  };

  const routeAfter = async () => {
    const done = await hasToday();
    window.location.assign(done ? HOME_PATH : MOODCALL_PATH);
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    (async () => { await fetchUser(); await routeAfter(); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGoogleSuccess = async (cred) => {
    setErr(""); setBusy(true);
    try {
      await ensureCsrf();
      const name = localStorage.getItem("guestName") || "";
      const reason = localStorage.getItem("userReason") || "";
      const { data } = await api.post(
        "/api/auth/google/",
        { id_token: cred.credential, visitor_id: visitorId, session_id: sessionId, name, reason },
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );
      localStorage.setItem("authToken", data.key);
      setAuthToken(data.key);
      await fetchUser();
      await routeAfter();
    } catch (e) {
      setErr(pick(e, "Google sign-in failed."));
    } finally { setBusy(false); }
  };

  const emailLogin = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await ensureCsrf();
      const { data } = await api.post("/api/auth/login/", { email, password: pwd });
      if (data?.key) {
        localStorage.setItem("authToken", data.key);
        await fetchUser();
        await routeAfter();
      }
    } catch (e2) { setErr(pick(e2, "Could not sign in with email/password.")); }
    finally { setBusy(false); }
  };

  const phoneClick = () => alert("Phone sign-in coming soon ✨");

  return (
    <div className="wp2-authcard">
      {/* Provider buttons */}
      <div className="wp2-prov">
        {/* Composite Google: visual pill + transparent real GIS overlay */}
        <div className="g-composite">
          <button type="button" className="btn-provider btn-google-visual" aria-hidden>
            {/* Official G mark SVG placed tight to the text */}
            <span className="g-ico" aria-hidden>
            <img src={GoogleG} alt="" className="g-img" />

            </span>
            <span className="prov-text">Continue with Google</span>
          </button>
          <div className="g-overlay">
            <GoogleLogin
              onSuccess={onGoogleSuccess}
              onError={() => setErr("Google sign-in failed.")}
              theme="outline"
              shape="pill"
              size="large"
              text="continue_with"
              width="100%"
            />
          </div>
        </div>

        {/* Phone button styled EXACTLY the same pill */}
        {/* TODO: Uncomment when phone auth feature is ready */}
        {/* <button type="button" className="btn-provider btn-provider-outline" onClick={phoneClick} disabled={busy}>
          <span className="prov-ico phone-ico" aria-hidden />
          <span className="prov-text">Continue with Phone</span>
        </button> */}
      </div>

      {/* Divider */}
      <div className="div-line" role="separator" aria-label="OR">
        <span className="div-chip">OR</span>
      </div>

      {/* Email sign-in */}
      <form className="wp2-form" onSubmit={emailLogin}>
        <input
          type="email"
          className="inpt"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <input
          type="password"
          className="inpt"
          placeholder="Password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          autoComplete="current-password"
          required
        />

        <div className="row-ops">
          <label className="chk">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span>Remember password</span>
          </label>
          <Link className="forgot" to="/reset-password">Forget your password</Link>
        </div>

        {err && <div className="error" role="alert">{err}</div>}

        <button type="submit" className="btn-continue" disabled={busy || !email || !pwd}>
          Continue
        </button>

        <p className="fine-print">
          By continuing, you acknowledge BloomPal’s{" "}
          <a className="link" href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          You may receive occasional product updates and tips.
        </p>
      </form>
    </div>
  );
}
